























































































































































import { FilterCheckboxOptionType } from '../atoms/ExpandableList.vue';
import { usePLPEssentials } from '@vue-storefront/novulo/';
import {
  computed,
  defineComponent,
  ref,
  set,
  watch,
  onMounted
} from '@nuxtjs/composition-api';
import useCommonFilters from '~/hooks/useCommonFilters';
import { ActivePriceFilterTypes } from './PriceFilter.vue';
import type { QuickFilterType } from './QuickFilters.vue';
import useDebouncer from '~/hooks/useDebouncer';
import { addCurrency, priceFormat } from '~/helpers/utils';

type FilterType = {
  _id: number;
  id: number;
  filter_name: string;
  filter_type?: number;
  filter_type_title: string;
  values?: FilterCheckboxOptionType[];
};

type ChipType = {
  name: string;
  optionId: number;
  filterId: number;
};

export const sortingOptions = [
  {
    value: 'cms_priority.desc',
    type: 'default',
    label: 'popularity',
    sortValue: 'popularity',
    dirValue: 'desc'
  },
  {
    value: 'sales_price.asc',
    type: 'default',
    label: 'price_low_high',
    sortValue: 'price',
    dirValue: 'asc'
  },
  {
    value: 'sales_price.desc',
    type: 'default',
    label: 'price_high_low',
    sortValue: 'price',
    dirValue: 'desc'
  }
];

export const buildApiParams = (search) => {
  const searchParams = new URLSearchParams(search);

  const buildItemParam = ({ searchParamsName, apiName }) => {
    return (
      searchParams
        .get(searchParamsName)
        ?.split(',')
        ?.map((id) => `${apiName}=${id}`)
        ?.join('&') || ''
    );
  };

  const categoriesParam = buildItemParam({
    searchParamsName: 'category',
    apiName: 'category'
  });
  const filtersParams = buildItemParam({
    searchParamsName: 'tags',
    apiName: 'qfv'
  });
  const brandsParams = buildItemParam({
    searchParamsName: 'brand',
    apiName: 'brands'
  });

  return search
    .replace('?', '&')
    .replace('price_min', 'from_price')
    .replace('price_max', 'to_price')
    .replace(/category=[^&]+/, categoriesParam)
    .replace(/tags=[^&]+/, filtersParams)
    .replace(/brand=[^&]+/, brandsParams);
};

export default defineComponent({
  name: 'FilterFacet',
  props: {
    selectedSort: {
      type: Object,
      required: true
    },
    totalResults: {
      type: Number,
      default: 0
    }
  },
  setup(_, context) {
    const plpId = computed(() => context.root.$store.getters['page/getPageId']);

    const { getPLPEssentials, PLPEssentialsResult, PLPEssentialsLoading } =
      usePLPEssentials();

    const { categoriesFilters, activeFilters, pricesData } = useCommonFilters();
    const filtersValues = ref([]);

    const selectedCategories = computed(() =>
      categoriesFilters.value.filter((f) => f.selected)
    );

    const sortItems = sortingOptions;

    const priceSelectionType = ref(null);
    const priceChips = ref([]);
    const formattedPriceChips = computed(() =>
      priceChips.value.map((chip) => ({
        name: `${addCurrency(
          priceFormat(chip.min, { hideFractionIfInt: true })
        )} - ${addCurrency(priceFormat(chip.max, { hideFractionIfInt: true }))}`
      }))
    );

    const { debounce } = useDebouncer();

    const setPriceChips = () => {
      if (
        !pricesData.priceRanges.length ||
        !pricesData.minPrice ||
        !pricesData.maxPrice
      )
        return;
      if (priceSelectionType.value !== ActivePriceFilterTypes.CHECKBOXES)
        if (
          pricesData.selectedPrice.min !== pricesData.minPrice ||
          pricesData.selectedPrice.max !== pricesData.maxPrice
        ) {
          priceChips.value = [
            {
              min: pricesData.selectedPrice.min,
              max: pricesData.selectedPrice.max
            }
          ];
        } else {
          priceChips.value = [];
        }
      else {
        priceChips.value = pricesData.priceRanges.filter(
          (range) =>
            range.min < pricesData.selectedPrice.max &&
            range.max > pricesData.selectedPrice.min
        );
      }
    };

    const setPriceSelectionType = (value) => {
      priceSelectionType.value = value;
      setPriceChips();
    };

    const insertCategoriesInfoInURL = () => {
      const urlValues = categoriesFilters.value
        .filter((f) => f.selected)
        .map((f) => f._id)
        .join(',');

      return urlValues;
    };

    const insertFiltersInfoInURL = () => {
      const brandFilters = activeFilters.value
        .filter((af) => af.filterURLTitle === 'brands')
        .map((af) => af.optionId)
        .join(',');

      const tagsFilters = activeFilters.value
        .filter((af) => af.filterURLTitle !== 'brands')
        .map((af) => af.optionId)
        .join(',');

      return { brandFilters, tagsFilters };
    };

    const fetchFilters = () => {
      debounce(() => {
        const params = buildApiParams(window.location.search);
        getPLPEssentials({
          id: plpId.value,
          params: params.replace(/&page=[^&]+/, '')
        });
      });
    };

    const removePriceChip = (index) => {
      const chip = priceChips.value[index];
      // if it's a single chip, reset the selection
      // if it's not an edge chip, don't do anything
      // if it's the first chip, set the min to the min of the next chip
      // if it's the last chip, set the max to the max of the previous chip
      if (priceChips.value.length === 1) {
        pricesData.selectedPrice.min = pricesData.minPrice;
        pricesData.selectedPrice.max = pricesData.maxPrice;
        priceSelectionType.value = null;
      } else if (
        chip.min > pricesData.selectedPrice.min &&
        chip.max < pricesData.selectedPrice.max
      ) {
        return;
      } else if (chip.min <= pricesData.selectedPrice.min) {
        pricesData.selectedPrice.min = priceChips.value[index + 1].min;
      } else if (chip.max >= pricesData.selectedPrice.max) {
        pricesData.selectedPrice.max = priceChips.value[index - 1].max;
      }
      setPriceChips();
      context.emit('updatePLP', {
        pricesData,
        priceSelectionType: priceSelectionType.value
      });
      fetchFilters();
    };

    const removeAllPriceChips = () => {
      pricesData.selectedPrice.min = pricesData.minPrice;
      pricesData.selectedPrice.max = pricesData.maxPrice;
      priceSelectionType.value = null;
      setPriceChips();
    };

    const removeCategoryChip = ({
      optionId,
      all = false
    }: {
      optionId?: number;
      all?: boolean;
    }) => {
      categoriesFilters.value = categoriesFilters.value.map((f) => ({
        ...f,
        selected: all || f._id === optionId ? false : f.selected
      }));
      if (all) return;
      const urlValues = insertCategoriesInfoInURL();
      context.emit('updatePLP', { urlValues });
      fetchFilters();
    };

    const getURLPrices = () => {
      const url = new URL((window as any).location);
      const min = url.searchParams.get('price_min');
      const max = url.searchParams.get('price_max');
      const type = url.searchParams.get('type');
      if (min) {
        pricesData.selectedPrice.min = parseFloat(min);
      }
      if (max) {
        pricesData.selectedPrice.max = parseFloat(max);
      }

      if (type) {
        priceSelectionType.value = type;
      }
      setPriceChips();
    };

    const updateSelectedPrice = ({ min, max }, type) => {
      pricesData.selectedPrice = { min, max };
      priceSelectionType.value = type;
      setPriceChips();
      context.emit('updatePLP', {
        pricesData,
        priceSelectionType: priceSelectionType.value
      });
      fetchFilters();
    };

    const handleToggleCategory = (value) => {
      const category = categoriesFilters.value.find(
        (f) => f._id === value.optionId
      );
      if (!category) return;
      categoriesFilters.value = categoriesFilters.value.map((f) => ({
        ...f,
        selected: f._id === value.optionId ? value.selected : f.selected
      }));
      const urlValues = insertCategoriesInfoInURL();
      context.emit('updatePLP', { urlValues });
      fetchFilters();
    };

    const getURLSelectedCategories = () => {
      const url = new URL((window as any).location);
      const categories = url.searchParams.get('category')?.split(',') || [];
      categoriesFilters.value = categoriesFilters.value.map((f) => ({
        ...f,
        selected: categories.includes(`${f._id}`)
      }));
    };

    const selectFiltersFromURL = () => {
      activeFilters.value = [];
      const url = new URL((window as any).location);
      const brandIds = url.searchParams.get('brand')?.split(',') || [];
      const tagsIds = url.searchParams.get('tags')?.split(',') || [];
      const brandFilters = filtersValues.value.find(
        (f: FilterType) => f.filter_type_title === 'brands'
      ) || { values: [] };

      for (const brand of brandIds) {
        brandFilters.values
          .filter((f: FilterCheckboxOptionType) => f._id === Number(brand))
          .forEach((f) => {
            set(f, 'selected', true);
            activeFilters.value.push({
              name: f.name,
              optionId: f._id,
              filterId: brandFilters.id,
              filterURLTitle: brandFilters.filter_type_title
            });
          });
      }

      const tagFilters =
        filtersValues.value.filter((f) => f.filter_type_title !== 'brands') ||
        [];
      for (const tag of tagsIds) {
        tagFilters.forEach((f) => {
          const option = f.values.find(
            (v: FilterCheckboxOptionType) => v._id === Number(tag)
          );

          if (option) {
            set(option, 'selected', true);
            activeFilters.value.push({
              name: option.name,
              optionId: option._id,
              filterId: f.id,
              filterURLTitle: f.filter_type_title
            });
          }
        });
      }
    };

    const updateFilter = (
      value: { name?: string; optionId: number; selected: boolean },
      filter: FilterType,
      fetchAgain = true
    ) => {
      set(
        filter.values.find(
          (v: FilterCheckboxOptionType) => v._id === value.optionId
        ),
        'selected',
        value.selected
      );

      if (value.selected) {
        if (activeFilters.value.every((af) => af.optionId !== value.optionId))
          activeFilters.value.push({
            name: value.name,
            optionId: value.optionId,
            filterId: filter.id,
            filterURLTitle: filter.filter_type_title
          });
      } else {
        activeFilters.value.splice(
          activeFilters.value.findIndex(
            (af: ChipType) => af.optionId === value.optionId
          ),
          1
        );
      }

      if (fetchAgain) {
        const { tagsFilters, brandFilters } = insertFiltersInfoInURL();
        const urlValues = insertCategoriesInfoInURL();
        context.emit('updatePLP', {
          tagsFilters,
          brandFilters,
          urlValues,
          pricesData,
          priceSelectionType: priceSelectionType.value
        });
        fetchFilters();
        selectFiltersFromURL();
      }
    };

    const removeFilter = (option: ChipType, fetchAgain = true) => {
      updateFilter(
        { optionId: option.optionId, selected: false },
        filtersValues.value.find((f: FilterType) => f.id === option.filterId),
        fetchAgain
      );
    };

    const removeAllFilters = (fetchAgainWhenRemoved = true) => {
      if (priceChips.value.length) {
        removeAllPriceChips();
      }

      removeCategoryChip({ all: true });

      const allActiveFilters = [...activeFilters.value];
      allActiveFilters.forEach((activeFilter) =>
        removeFilter(activeFilter, false)
      );

      if (!fetchAgainWhenRemoved) return;

      const { tagsFilters, brandFilters } = insertFiltersInfoInURL();
      const urlValues = insertCategoriesInfoInURL();
      context.emit('updatePLP', {
        brandFilters,
        tagsFilters,
        urlValues,
        pricesData,
        priceSelectionType: priceSelectionType.value
      });
      fetchFilters();
      selectFiltersFromURL();
    };

    onMounted(() => {
      // listen to event on root coming from QuickFilters
      context.root.$on(
        'quick-filter-toggle',
        ({
          quickFilter: { brand, min_price, max_price, filter_values },
          isSelecting
        }: {
          quickFilter: QuickFilterType;
          isSelecting: boolean;
        }) => {
          // create quick filter filters
          let quickFilterFilters = [];
          // create brand filter
          if (brand) {
            const brandValue = filtersValues.value?.[0]?.values?.find(
              (v) => v._id === brand
            );
            if (brandValue)
              quickFilterFilters.push({
                name: brandValue.name,
                optionId: brand,
                selected: isSelecting
              });
          }
          // create other filters
          if (filter_values) {
            quickFilterFilters = [
              ...quickFilterFilters,
              ...filter_values.map((filterValue) => ({
                name: filterValue.name,
                optionId: filterValue.qfv_id,
                selected: isSelecting
              }))
            ];
          }

          // update filters
          if (isSelecting) {
            removeAllFilters(false);
          }
          quickFilterFilters.forEach((filter) => {
            const filterToUpdate = [
              PLPEssentialsResult.value.filter_brands,
              ...(PLPEssentialsResult.value.filters || [])
            ].find((f) => f.values.some((v) => v._id === filter.optionId));
            if (filterToUpdate) {
              updateFilter(filter, filterToUpdate, false);
            }
          });

          // set price data
          if (isSelecting && min_price && max_price) {
            pricesData.selectedPrice.min = min_price;
            pricesData.selectedPrice.max = max_price;
            priceSelectionType.value = ActivePriceFilterTypes.QUICK_FILTER;
          }

          // fetch
          const { tagsFilters, brandFilters } = insertFiltersInfoInURL();
          context.emit('updatePLP', {
            brandFilters,
            tagsFilters,
            pricesData,
            priceSelectionType: priceSelectionType.value
          });
          fetchFilters();
          selectFiltersFromURL();
        }
      );
    });

    watch(
      () => PLPEssentialsLoading.value,
      (PLPEssentialsLoading) => {
        if (!PLPEssentialsLoading.value) {
          filtersValues.value = [
            PLPEssentialsResult.value.filter_brands,
            ...(PLPEssentialsResult.value.filters || [])
          ].filter((f: FilterType) =>
            f.values?.some(
              (v: FilterCheckboxOptionType) => v.number_of_products !== 0
            )
          );

          categoriesFilters.value =
            PLPEssentialsResult.value.primary_producttags
              ?.filter((f) => f.number_of_products !== 0)
              ?.map((f) => ({ ...f, selected: false })) || [];

          pricesData.minPrice = PLPEssentialsResult.value.minimum_price;
          pricesData.maxPrice = PLPEssentialsResult.value.maximum_price;
          pricesData.selectedPrice.min =
            PLPEssentialsResult.value.minimum_price;
          pricesData.selectedPrice.max =
            PLPEssentialsResult.value.maximum_price;
          pricesData.priceRanges = PLPEssentialsResult.value.price_range.map(
            (range) => ({
              min: range.from_price,
              max: range.to_price || PLPEssentialsResult.value.maximum_price,
              numberOfProducts: range.number_of_products
            })
          );

          if (process.client) {
            setTimeout(() => {
              selectFiltersFromURL();
              getURLPrices();
              getURLSelectedCategories();
            }, 0);
          }
        }
      },
      { immediate: true }
    );

    return {
      filtersValues,
      activeFilters,
      sortItems,
      pricesData,
      categoriesFilters,
      selectedCategories,
      formattedPriceChips,
      ActivePriceFilterTypes,
      priceSelectionType,
      setPriceSelectionType,
      removePriceChip,
      removeCategoryChip,
      updateSelectedPrice,
      handleToggleCategory,
      updateFilter,
      removeFilter,
      removeAllFilters
    };
  }
});
